import React from 'react';
import { NavigateAction, ToolbarProps } from 'react-big-calendar';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

// Définition de notre propre interface pour les props de la barre d'outils
interface CustomToolbarProps extends ToolbarProps {
  // Vous pouvez ajouter des props supplémentaires si nécessaire
}

const CustomToolbar: React.FC<CustomToolbarProps> = (props) => {
  const { t } = useTranslation();
  const { onNavigate, date, view, onView, views } = props;

  // Fonction pour formater la date en fonction de la vue actuelle
  const getFormattedDate = () => {
    switch (view) {
      case 'month':
        return dayjs(date).format('MMMM YYYY');
      case 'week':
        const startOfWeek = dayjs(date).startOf('week');
        const endOfWeek = dayjs(date).endOf('week');
        return `${startOfWeek.format('D')} - ${endOfWeek.format('D MMM YYYY')}`;
      case 'day':
        return dayjs(date).format('dddd D MMMM YYYY');
      case 'agenda':
        return `Agenda: ${dayjs(date).format('MMMM YYYY')}`;
      default:
        return dayjs(date).format('MMMM YYYY');
    }
  };

  // Style commun pour tous les boutons
  const buttonBaseClass =
    'inline-flex items-center px-4 py-2 text-sm font-medium border focus:z-10 focus:outline-none focus:ring-1 focus:ring-sky-500';
  const activeButtonClass = 'bg-sky-600 text-white border-sky-600 z-10';
  const inactiveButtonClass = 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50';

  return (
    <div className="flex flex-col px-4 py-3 mb-3 bg-white rounded-lg">
      {/* Première ligne : Groupe de navigation à gauche, vues à droite */}
      <div className="flex flex-wrap items-center">
        {/* Groupe de navigation (Aujourd'hui + flèches) */}
        <div className="flex-none mr-auto mb-2 sm:mb-0">
          <div className="inline-flex rounded-md shadow-sm">
            <button
              type="button"
              onClick={() => onNavigate('TODAY')}
              className={`${buttonBaseClass} ${inactiveButtonClass} rounded-l-md`}
            >
              {t('common.today')}
            </button>
            <button
              type="button"
              onClick={() => onNavigate('PREV')}
              className={`${buttonBaseClass} ${inactiveButtonClass}`}
            >
              <ChevronLeftIcon className="w-5 h-5" />
            </button>
            <button
              type="button"
              onClick={() => onNavigate('NEXT')}
              className={`${buttonBaseClass} ${inactiveButtonClass} rounded-r-md`}
            >
              <ChevronRightIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Date centrée, si largeur assez grande */}
        <div className="xl:flex hidden justify-center items-center pt-4 capitalize">
          <span className="text-lg font-medium text-gray-700 px-4">{getFormattedDate()}</span>
        </div>

        {/* Boutons de vue */}
        <div className="flex-none ml-auto sm:mb-0">
          <div className="inline-flex rounded-md shadow-sm">
            {views.map((name, i) => (
              <button
                key={name}
                type="button"
                onClick={() => onView(name)}
                className={`
                  ${buttonBaseClass}
                  ${view === name ? activeButtonClass : inactiveButtonClass}
                  ${i === 0 ? 'rounded-l-md' : ''}
                  ${i === views.length - 1 ? 'rounded-r-md' : ''}
                `}
              >
                {t(`common.${name.toLowerCase()}`)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Deuxième ligne : Date centrée, si largeur trop petite */}
      <div className="flex xl:hidden justify-center items-center pt-4">
        <span className="text-lg font-medium text-gray-700 px-4 capitalize">
          {getFormattedDate()}
        </span>
      </div>
    </div>
  );
};

export default CustomToolbar;
